import type { RouterContextType } from "@/api/permissions/permissions.api";
import AlignmentFormFooter from "@/components/features/StepperFormLayout/FormFooter/AlignmentFormFooter";
import { ConfirmationModal } from "@/components/ui/ConfirmationModal/ConfirmationModal";
import { authGuardWithPermissions } from "@/utils/authGuard";
import { Box, Stack, Text } from "@mantine/core";
import { IconX } from "@tabler/icons-react";
import { createFileRoute, Outlet, useNavigate, useParams } from "@tanstack/react-router";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import styles from "@/components/features/StepperFormLayout/formLayout.module.scss";

export const Route = createFileRoute("/service-providers/$id/alignment/_alignmentLayout")({
  component: AlignmentLayout,
  beforeLoad: async (args) => {
    await authGuardWithPermissions({
      context: args.context as RouterContextType,
      location: args.location as { pathname: string },
    });
  },
});

function AlignmentLayout() {
  const navigate = useNavigate();
  const { id } = useParams({ strict: false }) as { id?: string };
  const [isCancelModalOpen, setIsCancelModalOpen] = useState(false);

  // Sections save independently; this form context only carries the
  // coverage fields and section-registered save handlers (e.g. _ratesSaveHandler).
  const methods = useForm({
    mode: "all",
    reValidateMode: "onChange",
    criteriaMode: "all",
  });

  const handleClose = () => {
    if (id) {
      navigate({ to: "/service-providers/$id", params: { id } });
    } else {
      navigate({ to: "/service-providers" });
    }
  };

  return (
    <FormProvider {...methods}>
      <Box display={"flex"} style={{ height: "100vh" }}>
        <Stack className={styles.formLayout_container} gap={0}>
          <div className={styles.formLayout_header}>
            <Text className={styles.header_text}>Service Provider Alignment</Text>
            <IconX size={16} onClick={() => setIsCancelModalOpen(true)} cursor={"pointer"} />
          </div>
          <div className={styles.formLayout_body}>
            <Outlet />
          </div>
          <div className={styles.formLayout_footer}>
            <AlignmentFormFooter />
          </div>
        </Stack>

        <ConfirmationModal
          isOpen={isCancelModalOpen}
          onClose={() => setIsCancelModalOpen(false)}
          title={"Exit without completing?"}
          body={<Text>Are you sure you want to cancel? Any unsaved changes will be lost.</Text>}
          cancelText="Cancel"
          confirmText={"Confirm"}
          onConfirm={() => {
            setIsCancelModalOpen(false);
            handleClose();
          }}
        />
      </Box>
    </FormProvider>
  );
}
